import React, { useState, useEffect, useRef } from 'react';
import { Input, Select, Radio, Checkbox } from 'antd';

const defaultSettings = {
    regDays: 7,
    applyDays: 3,
    applyPeriod: 'Month',
    isAttendanceRulesEnabled: true,
    isEarlyOutRulesEnabled: true,
    isRelaxableSettingsEnabled: false,
    lateRuleType: 'count',
    rule1: {
        lateCount: 3,
        lateMinutes: 15,
        latePeriod: 'Month',
        lateDeduction: 'half',
        isLateMinutesDeductionEnabled: false,
        lateMinutesThreshold: 30,
        lateMinutesDeduction: 'half'
    },
    rule2: {
        lateMinutes: 15,
        lateTimeUnit: 'minute',
        lateTimeDeduction: 'single'
    },
    earlyOutRuleType: 'count',
    earlyOutRule1: {
        earlyOutCount: 3,
        earlyOutMinutes: 15,
        earlyOutPeriod: 'Month',
        earlyOutDeduction: 'half'
    },
    earlyOutRule2: {
        earlyOutMinutes: 15,
        earlyOutTimeUnit: 'minute',
        earlyOutTimeDeduction: '1'
    },
    relaxable: {
        requiredHours: 9,
        period: 'Day',
        flexibleStartHours: 2,
        lateArrivalDeduction: 'quarter',
        relaxableRuleType: 'lateArrival'
    }
};

const timeToMinutes = (timeStr) => {
    if (!timeStr || timeStr === '-') return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
};

export const resolveRelaxableRuleType = (relaxable = {}) => {
    const raw = relaxable.relaxableRuleType || relaxable.detectionType;
    if (!raw) return 'lateArrival';

    const normalized = String(raw).replace(/\s+/g, '').toLowerCase();
    if (normalized === 'detecthourly') return 'detectHourly';
    if (normalized === 'nodedection') return 'noDetection';
    if (normalized === 'latearrival') return 'lateArrival';

    return 'lateArrival';
};

const applyEarlyOutStatus = (status, adjustedCheckOutMinutes, adjustedEndTimeMinutes) => {
    if (adjustedCheckOutMinutes === null) return status;
    if (adjustedCheckOutMinutes >= adjustedEndTimeMinutes) return status;

    if (status === 'Late') return 'Late & Early Out';
    if (status === 'Present') return 'Early Out';
    return status;
};

export const calculateAttendanceStatus = ({ checkIn, checkOut, shift, settings }) => {
    if (!shift || !settings) return 'Present';
    if (!checkIn || checkIn === '-') return 'Missing';

    const startTimeMinutes = timeToMinutes(shift.startTime);
    const endTimeMinutes = timeToMinutes(shift.endTime);
    const checkInMinutes = timeToMinutes(checkIn);
    const checkOutMinutes = checkOut && checkOut !== '-' ? timeToMinutes(checkOut) : null;

    let adjustedEndTimeMinutes = endTimeMinutes;
    let adjustedCheckOutMinutes = checkOutMinutes;

    if (shift.dayChange && endTimeMinutes < startTimeMinutes) {
        if (checkOutMinutes !== null && checkOutMinutes < startTimeMinutes) {
            adjustedCheckOutMinutes = checkOutMinutes + 24 * 60;
        }
        adjustedEndTimeMinutes = endTimeMinutes + 24 * 60;
    }

    if (settings.isRelaxableSettingsEnabled && settings.relaxable) {
        const ruleType = resolveRelaxableRuleType(settings.relaxable);
        const graceMinutes = Number(settings.relaxable.flexibleStartHours) || 0;
        const flexibleStartMinutes = startTimeMinutes + graceMinutes;
        let status = 'Present';

        if (ruleType === 'lateArrival') {
            status = checkInMinutes > flexibleStartMinutes ? 'Late' : 'Present';
            return applyEarlyOutStatus(status, adjustedCheckOutMinutes, adjustedEndTimeMinutes);
        }

        if (ruleType === 'detectHourly') {
            if (checkOutMinutes !== null) {
                let checkout = adjustedCheckOutMinutes;
                if (shift.dayChange && checkout < checkInMinutes) {
                    checkout += 24 * 60;
                }

                const workingMinutes = checkout - checkInMinutes;
                const requiredMinutes =
                    (Number(settings.relaxable.requiredHours) || shift.workingHours || 9) * 60;

                status = workingMinutes >= requiredMinutes ? 'Present' : 'Incomplete';
            }

            return applyEarlyOutStatus(status, adjustedCheckOutMinutes, adjustedEndTimeMinutes);
        }

        return 'Present';
    }

    let status = 'Present';

    if (settings.isAttendanceRulesEnabled) {
        if (settings.lateRuleType === 'time') {
            const lateThresholdMinutes = startTimeMinutes + Number(settings.rule2?.lateMinutes || 0);
            if (checkInMinutes > lateThresholdMinutes) {
                status = 'Late';
            }
        } else if (settings.lateRuleType === 'count') {
            const graceMinutes = Number(settings.rule1?.lateMinutes || 0);
            const lateThresholdMinutes = startTimeMinutes + graceMinutes;
            if (checkInMinutes > lateThresholdMinutes) {
                status = 'Late';
            }
        }
    }

    if (settings.isEarlyOutRulesEnabled && checkOutMinutes !== null) {
        let earlyOutThresholdMinutes = adjustedEndTimeMinutes;

        if (settings.earlyOutRuleType === 'time') {
            earlyOutThresholdMinutes =
                adjustedEndTimeMinutes - Number(settings.earlyOutRule2?.earlyOutMinutes || 0);
        } else {
            earlyOutThresholdMinutes =
                adjustedEndTimeMinutes - Number(settings.earlyOutRule1?.earlyOutMinutes || 0);
        }

        status = applyEarlyOutStatus(status, adjustedCheckOutMinutes, earlyOutThresholdMinutes);
    }

    return status;
};

export const normalizeRelaxableForForm = (relaxable = {}) => ({
    ...defaultSettings.relaxable,
    ...relaxable,
    lateArrivalDeduction: ['quarter', 'half'].includes(relaxable.lateArrivalDeduction)
        ? relaxable.lateArrivalDeduction
        : defaultSettings.relaxable.lateArrivalDeduction,
    relaxableRuleType: resolveRelaxableRuleType(relaxable)
});

export const sanitizeRelaxableSettings = (relaxable = {}) => {
    const relaxableRuleType = resolveRelaxableRuleType(relaxable);

    if (relaxableRuleType === 'lateArrival') {
        return {
            flexibleStartHours: relaxable.flexibleStartHours ?? defaultSettings.relaxable.flexibleStartHours,
            lateArrivalDeduction: ['quarter', 'half'].includes(relaxable.lateArrivalDeduction)
                ? relaxable.lateArrivalDeduction
                : defaultSettings.relaxable.lateArrivalDeduction,
            relaxableRuleType: 'lateArrival'
        };
    }

    if (relaxableRuleType === 'detectHourly') {
        return { relaxableRuleType: 'detectHourly' };
    }

    return { relaxableRuleType: 'noDetection' };
};

// Function to migrate old settings structure to new structure
export const migrateSettings = (oldSettings) => {
    if (!oldSettings) return defaultSettings;
    
    // If it already has the new structure, return as is
    if (oldSettings.earlyOutRule1 && oldSettings.earlyOutRule2) {
        return {
            ...oldSettings,
            rule1: {
                ...defaultSettings.rule1,
                ...oldSettings.rule1
            },
            relaxable: normalizeRelaxableForForm(oldSettings.relaxable)
        };
    }
    
    // Migrate from old structure to new structure
    const migratedSettings = {
        ...defaultSettings,
        ...oldSettings,
        // Set default early out rule type
        earlyOutRuleType: 'count',
        // Create new early out rule structures
        earlyOutRule1: {
            earlyOutCount: oldSettings.earlyOutCount || 3,
            earlyOutMinutes: oldSettings.earlyOutMinutes || 15,
            earlyOutPeriod: oldSettings.earlyOutPeriod || 'Month',
            earlyOutDeduction: oldSettings.earlyOutDeduction || 'half'
        },
        earlyOutRule2: {
            earlyOutMinutes: 15,
            earlyOutTimeUnit: 'minute',
            earlyOutTimeDeduction: '1'
        }
    };
    
    // Remove old properties to avoid confusion
    delete migratedSettings.earlyOutCount;
    delete migratedSettings.earlyOutMinutes;
    delete migratedSettings.earlyOutPeriod;
    delete migratedSettings.earlyOutDeduction;

    migratedSettings.relaxable = normalizeRelaxableForForm(migratedSettings.relaxable);
    
    return migratedSettings;
};

const AttendanceRules = ({ settingsData, setSettingsData }) => {
    const [formData, setFormData] = useState(migrateSettings(settingsData));
    const isInternalUpdate = useRef(false);

    // Sync formData with settingsData prop (for edit mode)
    useEffect(() => {
        if (isInternalUpdate.current) {
            isInternalUpdate.current = false;
            return;
        }

        if (settingsData) {
            setFormData(migrateSettings(settingsData));
        } else {
            setFormData(defaultSettings);
        }
    }, [settingsData]);

    useEffect(() => {
        if (!setSettingsData) return;

        isInternalUpdate.current = true;
        setSettingsData({
            ...formData,
            relaxable: sanitizeRelaxableSettings(formData.relaxable)
        });
    }, [formData, setSettingsData]);

    const handleInputChange = (name, value) => {
        if (name === 'isAttendanceRulesEnabled' || name === 'isEarlyOutRulesEnabled') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                isRelaxableSettingsEnabled: false
            }));
        } else if (name === 'isRelaxableSettingsEnabled') {
            setFormData(prev => ({
                ...prev,
                isRelaxableSettingsEnabled: value,
                isAttendanceRulesEnabled: false,
                isEarlyOutRulesEnabled: false
            }));
        } else if (name === 'lateRuleType') {
            setFormData(prev => ({
                ...prev,
                lateRuleType: value,
                rule1: value === 'count' ? prev.rule1 : {
                    lateCount: '',
                    lateMinutes: '',
                    latePeriod: 'Month',
                    lateDeduction: 'half',
                    isLateMinutesDeductionEnabled: false,
                    lateMinutesThreshold: '',
                    lateMinutesDeduction: 'half'
                },
                rule2: value === 'time' ? prev.rule2 : {
                    lateMinutes: '',
                    lateTimeUnit: 'minute',
                    lateTimeDeduction: '1'
                }
            }));
        } else if (name === 'earlyOutRuleType') {
            setFormData(prev => ({
                ...prev,
                earlyOutRuleType: value,
                earlyOutRule1: value === 'count' ? prev.earlyOutRule1 : {
                    earlyOutCount: '',
                    earlyOutMinutes: '',
                    earlyOutPeriod: 'Month',
                    earlyOutDeduction: 'half'
                },
                earlyOutRule2: value === 'time' ? prev.earlyOutRule2 : {
                    earlyOutMinutes: '',
                    earlyOutTimeUnit: 'minute',
                    earlyOutTimeDeduction: '1'
                }
            }));
        } else if (name.startsWith('rule1.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                rule1: {
                    ...prev.rule1,
                    [field]: value
                }
            }));
        } else if (name.startsWith('rule2.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                rule2: {
                    ...prev.rule2,
                    [field]: value
                }
            }));
        } else if (name.startsWith('earlyOutRule1.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                earlyOutRule1: {
                    ...prev.earlyOutRule1,
                    [field]: value
                }
            }));
        } else if (name.startsWith('earlyOutRule2.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                earlyOutRule2: {
                    ...prev.earlyOutRule2,
                    [field]: value
                }
            }));
        } else if (name.startsWith('relaxable.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                relaxable: {
                    ...prev.relaxable,
                    [field]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };



    return (
        <div className="p-4">
            <div className="col-span-12 border border-gray-200 p-4 rounded-md bg-[#F4FAFF] mb-4">
                <h5 className="font-semibold text-md mb-2">
                    Attendance Regularization Settings
                </h5>
                <div className="flex items-center gap-2">
                    <span className="text-md">
                        Regularization request can be raised
                    </span>
                    <Input 
                        name="regDays" 
                        className="!w-12" 
                        value={formData.regDays}
                        onChange={(e) => handleInputChange('regDays', e.target.value)}
                    />
                    <span className="text-md">
                        day(s) from the date to be regularized
                    </span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                    <Input 
                        name="applyDays" 
                        className="!w-12" 
                        value={formData.applyDays}
                        onChange={(e) => handleInputChange('applyDays', e.target.value)}
                    />
                    <span className="text-md">day(s) a</span>
                    <Select 
                        value={formData.applyPeriod}
                        onChange={(value) => handleInputChange('applyPeriod', value)}
                    >
                        <option value="Day">Day</option>
                        <option value="Week">Week</option>
                        <option value="Month">Month</option>
                    </Select>
                </div>
            </div>

            <div className="col-span-12 border border-gray-200 p-4 rounded-md bg-[#F4FAFF] mb-4">
                <div className="flex items-center gap-2 mb-2">
                    <Checkbox 
                        checked={formData.isAttendanceRulesEnabled}
                        onChange={(e) => handleInputChange('isAttendanceRulesEnabled', e.target.checked)}
                    />
                    <h5 className="font-semibold text-md">
                        Attendance Rules
                    </h5>
                </div>
                
                {formData.isAttendanceRulesEnabled && (
                    <div className="mb-4">
                        <div className="mb-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Radio value="count" checked={formData.lateRuleType === 'count'} 
                                    onChange={(e) => handleInputChange('lateRuleType', 'count')} />
                                <h6 className="font-medium">Rule 1: Count Based</h6>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-md">If employee is late</span>
                                <Input 
                                    name="rule1.lateCount" 
                                    className="!w-12" 
                                    value={formData.rule1.lateCount}
                                    onChange={(e) => handleInputChange('rule1.lateCount', e.target.value)}
                                    disabled={formData.lateRuleType !== 'count'}
                                />
                                <span className="text-md">times</span>
                                <Input 
                                    name="rule1.lateMinutes" 
                                    className="!w-12" 
                                    value={formData.rule1.lateMinutes}
                                    onChange={(e) => handleInputChange('rule1.lateMinutes', e.target.value)}
                                    disabled={formData.lateRuleType !== 'count'}
                                />
                                <span className="text-md">minutes in a</span>
                                <Select 
                                    value={formData.rule1.latePeriod}
                                    onChange={(value) => handleInputChange('rule1.latePeriod', value)}
                                    className="!w-24"
                                    disabled={formData.lateRuleType !== 'count'}
                                >
                                    {/* <option value="Week">Week</option> */}
                                    <option value="Month">Month</option>
                                </Select>
                                <span className="text-md">Then deduct</span>
                                <Select 
                                    value={formData.rule1.lateDeduction}
                                    onChange={(value) => handleInputChange('rule1.lateDeduction', value)}
                                    className="!w-24"
                                    disabled={formData.lateRuleType !== 'count'}
                                >
                                    <option value="half">Half Day</option>
                                    <option value="full">Full Day</option>
                                </Select>
                            </div>
                            <div className="mt-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Checkbox
                                        checked={formData.rule1.isLateMinutesDeductionEnabled}
                                        onChange={(e) => handleInputChange('rule1.isLateMinutesDeductionEnabled', e.target.checked)}
                                        disabled={formData.lateRuleType !== 'count'}
                                    />
                                    <span className="text-md">Late minutes deduction</span>
                                </div>
                                {formData.rule1.isLateMinutesDeductionEnabled && (
                                    <div className="flex items-center gap-2 ml-6">
                                        <span className="text-md">If employee is late</span>
                                        <Input
                                            name="rule1.lateMinutesThreshold"
                                            className="!w-12"
                                            value={formData.rule1.lateMinutesThreshold}
                                            onChange={(e) => handleInputChange('rule1.lateMinutesThreshold', e.target.value)}
                                            disabled={formData.lateRuleType !== 'count'}
                                        />
                                        <span className="text-md">minutes, then deduct</span>
                                        <Select
                                            value={formData.rule1.lateMinutesDeduction}
                                            onChange={(value) => handleInputChange('rule1.lateMinutesDeduction', value)}
                                            className="!w-24"
                                            disabled={formData.lateRuleType !== 'count'}
                                        >
                                            <option value="half">Half Day</option>
                                            <option value="full">Full Day</option>
                                        </Select>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-6">
                            <div className="flex items-center gap-2 mb-2">
                                <Radio value="time" checked={formData.lateRuleType === 'time'}
                                    onChange={() => handleInputChange('lateRuleType', 'time')} />
                                <h6 className="font-medium">Rule 2: Time</h6>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-md">Late by</span>
                                <Input 
                                    name="rule2.lateMinutes" 
                                    className="!w-12" 
                                    value={formData.rule2.lateMinutes}
                                    onChange={(e) => handleInputChange('rule2.lateMinutes', e.target.value)}
                                    disabled={formData.lateRuleType !== 'time'}
                                />
                                <Select 
                                    value={formData.rule2.lateTimeUnit}
                                    onChange={(value) => handleInputChange('rule2.lateTimeUnit', value)}
                                    className="!w-20"
                                    disabled={formData.lateRuleType !== 'time'}
                                >
                                    <option value="minute">Minute(s)</option>
                                    <option value="hour">Hour(s)</option>
                                </Select>
                                <span className="text-md">or more, deduct</span>
                                <Select 
                                    value={formData.rule2.lateTimeDeduction}
                                    onChange={(value) => handleInputChange('rule2.lateTimeDeduction', value)}
                                    className="!w-16"
                                    disabled={formData.lateRuleType !== 'time'}
                                >
                                    <option value="single">1x</option>
                                    <option value="1.5">1.5x</option>
                                    <option value="double">2x</option>
                                    <option value="triple">3x</option>
                                </Select>
                                <span className="text-md">
                                    salary per {formData.rule2.lateTimeUnit === 'minute' ? 'minute' : formData.rule2.lateTimeUnit === 'hour' ? 'hour' : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="col-span-12 border border-gray-200 p-4 rounded-md bg-[#F4FAFF] mb-4">
                <div className="flex items-center gap-2 mb-2">
                    <Checkbox 
                        checked={formData.isEarlyOutRulesEnabled}
                        onChange={(e) => handleInputChange('isEarlyOutRulesEnabled', e.target.checked)}
                    />
                    <h5 className="font-semibold text-md">
                        Early Out Rules
                    </h5>
                </div>
                
                {formData.isEarlyOutRulesEnabled && (
                    <div className="mb-4">
                        <div className="mb-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Radio value="count" checked={formData.earlyOutRuleType === 'count'} 
                                    onChange={(e) => handleInputChange('earlyOutRuleType', 'count')} />
                                <h6 className="font-medium">Rule 1: Count Based</h6>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-md">If employee leaves early</span>
                                <Input 
                                    name="earlyOutRule1.earlyOutCount" 
                                    className="!w-12" 
                                    value={formData.earlyOutRule1.earlyOutCount}
                                    onChange={(e) => handleInputChange('earlyOutRule1.earlyOutCount', e.target.value)}
                                    disabled={formData.earlyOutRuleType !== 'count'}
                                />
                                <span className="text-md">times by</span>
                                <Input 
                                    name="earlyOutRule1.earlyOutMinutes" 
                                    className="!w-12" 
                                    value={formData.earlyOutRule1.earlyOutMinutes}
                                    onChange={(e) => handleInputChange('earlyOutRule1.earlyOutMinutes', e.target.value)}
                                    disabled={formData.earlyOutRuleType !== 'count'}
                                />
                                <span className="text-md">minutes or more in a</span>
                                <Select 
                                    value={formData.earlyOutRule1.earlyOutPeriod}
                                    onChange={(value) => handleInputChange('earlyOutRule1.earlyOutPeriod', value)}
                                    className="!w-24"
                                    disabled={formData.earlyOutRuleType !== 'count'}
                                >
                                    <option value="Month">Month</option>
                                </Select>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                                <span className="text-md">Then deduct</span>
                                <Select 
                                    value={formData.earlyOutRule1.earlyOutDeduction}
                                    onChange={(value) => handleInputChange('earlyOutRule1.earlyOutDeduction', value)}
                                    className="!w-24"
                                    disabled={formData.earlyOutRuleType !== 'count'}
                                >
                                    <option value="half">Half Day</option>
                                    <option value="full">Full Day</option>
                                </Select>
                            </div>
                        </div>

                        <div className="mt-6">
                            <div className="flex items-center gap-2 mb-2">
                                <Radio value="time" checked={formData.earlyOutRuleType === 'time'}
                                    onChange={() => handleInputChange('earlyOutRuleType', 'time')} />
                                <h6 className="font-medium">Rule 2: Time</h6>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-md">Early out by</span>
                                <Input 
                                    name="earlyOutRule2.earlyOutMinutes" 
                                    className="!w-12" 
                                    value={formData.earlyOutRule2.earlyOutMinutes}
                                    onChange={(e) => handleInputChange('earlyOutRule2.earlyOutMinutes', e.target.value)}
                                    disabled={formData.earlyOutRuleType !== 'time'}
                                />
                                <Select 
                                    value={formData.earlyOutRule2.earlyOutTimeUnit}
                                    onChange={(value) => handleInputChange('earlyOutRule2.earlyOutTimeUnit', value)}
                                    className="!w-20"
                                    disabled={formData.earlyOutRuleType !== 'time'}
                                >
                                    <option value="minute">Minute(s)</option>
                                    <option value="hour">Hour(s)</option>
                                </Select>
                                <span className="text-md">or more, deduct</span>
                                <Select 
                                    value={formData.earlyOutRule2.earlyOutTimeDeduction}
                                    onChange={(value) => handleInputChange('earlyOutRule2.earlyOutTimeDeduction', value)}
                                    className="!w-16"
                                    disabled={formData.earlyOutRuleType !== 'time'}
                                >
                                    <option value="1">1x</option>
                                    <option value="1.5">1.5x</option>
                                    <option value="2">2x</option>
                                    <option value="2.5">2.5x</option>
                                    <option value="3">3x</option>
                                </Select>
                                <span className="text-md">
                                    salary per {formData.earlyOutRule2.earlyOutTimeUnit === 'minute' ? 'minute' : formData.earlyOutRule2.earlyOutTimeUnit === 'hour' ? 'hour' : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="col-span-12 border border-gray-200 p-4 rounded-md bg-[#F4FAFF] mb-4">
                <div className="flex items-center gap-2 mb-2">
                    <Checkbox 
                        checked={formData.isRelaxableSettingsEnabled}
                        onChange={(e) => handleInputChange('isRelaxableSettingsEnabled', e.target.checked)}
                    />
                    <h5 className="font-semibold text-md">
                        Relaxable Settings
                    </h5>
                </div>
                {formData.isRelaxableSettingsEnabled && (
                    <>
                        <div className="flex items-center gap-2">
                            <span className="text-md">Employees should work</span>
                            <Input 
                                name="relaxable.requiredHours" 
                                className="!w-12" 
                                value={formData.relaxable.requiredHours}
                                onChange={(e) => handleInputChange('relaxable.requiredHours', e.target.value)}
                            />
                            <span className="text-md">hours </span>
                            <Select 
                                value={formData.relaxable.period}
                                onChange={(value) => handleInputChange('relaxable.period', value)}
                                className="!w-24"
                            >
                                <option value="Day">Day</option>
                                {/* <option value="Week">Week</option>
                                <option value="Month">Month</option> */}
                            </Select>
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Radio
                                    checked={formData.relaxable.relaxableRuleType === 'lateArrival'}
                                    onChange={() => handleInputChange('relaxable.relaxableRuleType', 'lateArrival')}
                                />
                                <span className="text-md">Employee can arrive up to</span>
                                <Input 
                                    name="relaxable.flexibleStartHours" 
                                    className="!w-12" 
                                    value={formData.relaxable.flexibleStartHours}
                                    onChange={(e) => handleInputChange('relaxable.flexibleStartHours', e.target.value)}
                                    disabled={formData.relaxable.relaxableRuleType !== 'lateArrival'}
                                />
                                <span className="text-md">minutes late from the official start time, then deduct</span>
                                <Select
                                    value={formData.relaxable.lateArrivalDeduction}
                                    onChange={(value) => handleInputChange('relaxable.lateArrivalDeduction', value)}
                                    className="!w-32"
                                    disabled={formData.relaxable.relaxableRuleType !== 'lateArrival'}
                                >
                                    <option value="quarter">Quarter Day</option>
                                    <option value="half">Half Day</option>
                                </Select>
                            </div>
                            <div className="flex items-center gap-2">
                                <Radio
                                    checked={formData.relaxable.relaxableRuleType === 'detectHourly'}
                                    onChange={() => handleInputChange('relaxable.relaxableRuleType', 'detectHourly')}
                                />
                                <span className="text-md">
                                    If employee has not completed {formData.relaxable.requiredHours} hour(s) then Detect Hourly
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Radio
                                    checked={formData.relaxable.relaxableRuleType === 'noDetection'}
                                    onChange={() => handleInputChange('relaxable.relaxableRuleType', 'noDetection')}
                                />
                                <span className="text-md">No Detection</span>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default AttendanceRules; 